import { QueryClient } from '@tanstack/react-query';
import Logger from '../logger';
import { HttpError } from './HttpError';

// Re-export HttpError for backward compatibility
export { HttpError, isAuthTimeExceededError } from './HttpError';

// Simple UUID v4 generator
function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface HttpClientConfig {
  baseURL: string;
  headers?: Record<string, string>;
  timeout?: number;
  // Query Client options (flattened)
  staleTime?: number;
  gcTime?: number;
  retry?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  // Mutation options
  mutationRetry?: number;
}

export class PublicHttpClient {
  protected baseURL: string;
  protected queryClient: QueryClient;
  protected headers?: Record<string, string>;
  protected timeout?: number;

  constructor(config: HttpClientConfig) {
    this.baseURL = config.baseURL;
    this.headers = config.headers;
    this.timeout = config.timeout;
    // Create QueryClient internally with default options
    this.queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: config.staleTime ?? 5 * 60 * 1000, // Default: 5 minutes
          gcTime: config.gcTime ?? 10 * 60 * 1000, // Default: 10 minutes
          retry: config.retry ?? 3, // Default: 3 retries
          refetchOnWindowFocus: config.refetchOnWindowFocus ?? false,
          refetchOnReconnect: config.refetchOnReconnect ?? true,
        },
        mutations: {
          retry: config.mutationRetry ?? 1, // Default: 1 retry
        },
      },
    });
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  getQueryClient(): QueryClient {
    return this.queryClient;
  }

  getHeaders(): Record<string, string> | undefined {
    return this.headers;
  }

  getTimeout(): number | undefined {
    return this.timeout;
  }

  async request<TResponse>(
    path: string,
    options: RequestInit & {
      params?: Record<string, string | number>;
      headers?: Record<string, string>;
    } = {},
  ): Promise<TResponse> {
    // Properly handle baseURL with path components (e.g., /console or /iam)
    // new URL() with absolute paths (starting with /) discards the base path
    const baseUrl = new URL(this.baseURL);
    const basePath = baseUrl.pathname.replace(/\/$/, ''); // Remove trailing slash

    // Separate path from any query parameters already in the path
    // This prevents query params from being encoded when set via pathname
    const [pathOnly, queryString] = path.split('?');
    const cleanPath = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
    baseUrl.pathname = `${basePath}${cleanPath}`;

    // Add query params from the path string (e.g., ?accountId=123)
    if (queryString) {
      const pathParams = new URLSearchParams(queryString);
      pathParams.forEach((value, key) => {
        baseUrl.searchParams.append(key, value);
      });
    }

    const url = baseUrl;

    // Add query params if provided via options
    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const headers = {
      'Content-Type': 'application/json',
      ...this.headers,
      ...options.headers,
      'tcp-request-id': uuidv4(),
    };

    const fetchOptions: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url.toString(), fetchOptions);
      if (!response.ok) {
        let detail: string | undefined;
        let body: unknown;
        try {
          body = await response.json();
          const errorBody = body as { detail?: string } | null;
          if (errorBody && typeof errorBody.detail === 'string') {
            detail = errorBody.detail;
          }
        } catch {
          // ignore parse failures — detail stays undefined
        }
        throw new HttpError(response.status, response.statusText, detail, body);
      }

      // Handle empty responses (204 No Content or empty body)
      const contentLength = response.headers.get('content-length');
      if (response.status === 204 || contentLength === '0') {
        return undefined as TResponse;
      }

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return undefined as TResponse;
      }

      // Try to parse JSON, return undefined if empty
      const text = await response.text();
      if (!text || text.trim() === '') {
        return undefined as TResponse;
      }

      return JSON.parse(text) as TResponse;
    } catch (error) {
      Logger.error(`Request failed: ${options.method || 'GET'} ${url.toString()}`, error);
      throw error;
    }
  }
}
